import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { apiFetch } from '@/lib/api';

export const Route = createFileRoute('/verify-email')({
  component: VerifyEmailPage,
});

function VerifyEmailPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'pending' | 'ok' | 'error'>('pending');
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) {
      setStatus('error');
      setError('token_missing');
      return;
    }
    (async () => {
      try {
        const res = await apiFetch(`/auth/verify-email?token=${encodeURIComponent(token)}`);
        const j = (await res.json().catch(() => ({}))) as {
          ok?: boolean;
          error?: string;
          email?: string;
        };
        if (res.ok && j.ok) {
          setStatus('ok');
          setEmail(j.email ?? null);
        } else {
          setStatus('error');
          setError(j.error ?? 'unknown');
        }
      } catch {
        setStatus('error');
        setError('network');
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-line bg-card p-8 text-center shadow-soft">
        <img
          src="/brand/logo-icon-256.png"
          alt="4 M Immobilien"
          width={56}
          height={56}
          className="mx-auto mb-4"
        />
        {status === 'pending' && (
          <>
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-ink-3" />
            <h1 className="mt-3 font-display text-xl text-ink">E-Mail wird bestätigt …</h1>
          </>
        )}
        {status === 'ok' && (
          <>
            <CheckCircle2 className="mx-auto h-12 w-12 text-[hsl(var(--success))]" />
            <h1 className="mt-4 font-display text-xl text-ink">E-Mail bestätigt</h1>
            <p className="mt-2 text-sm text-ink-2">
              Ihre neue E-Mail-Adresse {email ? <strong>{email}</strong> : ''} ist jetzt
              aktiv.
            </p>
            <Link
              to="/dashboard"
              className="mt-6 inline-flex rounded-md px-5 py-2 font-semibold text-[#1a1305] shadow-soft"
              style={{
                background:
                  'linear-gradient(135deg, hsl(var(--brand-gold)), hsl(var(--brand-gold-deep)))',
              }}
            >
              Zum Dashboard
            </Link>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle className="mx-auto h-12 w-12 text-[hsl(var(--danger))]" />
            <h1 className="mt-4 font-display text-xl text-ink">Bestätigung fehlgeschlagen</h1>
            <p className="mt-2 text-sm text-ink-2">
              {error === 'token_missing' && t('auth.verify_email.token_missing', 'Bestätigungs-Token fehlt im Link.')}
              {error === 'token_invalid' && t('auth.verify_email.token_invalid', 'Bestätigungs-Token ungültig.')}
              {error === 'token_already_used' &&
                t(
                  'auth.verify_email.token_already_used',
                  'Dieser Link wurde bereits verwendet. Falls Ihre Adresse trotzdem nicht aktiv ist, fordern Sie eine neue Bestätigung an.',
                )}
              {error === 'token_expired' &&
                t(
                  'auth.verify_email.token_expired',
                  'Bestätigungs-Link abgelaufen (24 h). Bitte E-Mail-Adresse erneut speichern.',
                )}
              {!['token_missing', 'token_invalid', 'token_already_used', 'token_expired'].includes(
                error ?? '',
              ) && t('auth.verify_email.unexpected', 'Unerwarteter Fehler. Versuchen Sie es später noch einmal.')}
            </p>
            <Link
              to="/login"
              className="mt-6 inline-flex rounded-md border border-line bg-card px-5 py-2 font-semibold text-ink hover:bg-paper-2"
            >
              Zur Anmeldung
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
